import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { searchDice, fetchJobBoardCandidate, DiceSearch as DiceSearchModel } from 'wisestep-api';
import { WsFilterTags, WsPagination, WsListingLoader } from 'wisestep-base';
import * as yup from 'yup';

import { PageHeader } from '../../../components/PageHeader';
import { ITrackCvViewJobBoards } from '../../../features/conversationWidget/utils/cvHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import TalentPoolListSideNav from '../../candidates/talentpool/components/talentpoolListSideNav';
import BotCustomization from '../Bots/customization/BotCustomizaton';
import CandidateCard from '../components/CandidateCard';
import NativeSearchForm from '../components/NativeSearchForm';
import BasicSearchForm from '../types/IBasicSearchForm';
import IQueryParam from '../types/IQueryParam';
import basicSearchDefaultValues from '../utils/nativeSearchHelper';

import AdvanceFilter from './components/AdvanceFilters';
import DiceAdvanceSearch, { AdvancedSearchKeys } from './types/IDiceAdvanceSearch';
import constructQuery from './utils/constructQuery';
import defaultAdvanceFilterValues from './utils/defaultAdvanceFilterValues';
import {
  defaultDiceSearchStateReq,
  getDiceCandidateDeatails,
  IDiceSeaarchStateProps,
  pushSearchFilters,
} from './utils/DiceHelper';

const DiceSearch = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [state, setState] = useState<IDiceSeaarchStateProps>(defaultDiceSearchStateReq);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const methods = useForm<BasicSearchForm & DiceAdvanceSearch>({
    defaultValues: { ...basicSearchDefaultValues, ...defaultAdvanceFilterValues, lastActive: '' },
    resolver: yupResolver(
      yup.object().shape({ query: yup.string().required('Search query is required') })
    ),
  });
  const setAdvanceFilterField = (targetField: AdvancedSearchKeys, value) =>
    methods.setValue(targetField, value);
  const emptyAdvanceFieldValues = {
    ...defaultAdvanceFilterValues,
    ...{ excludeSocialOnlyProfiles: false, additionalFilters: [], contactMethod: [] },
  };
  const selectedIndex = (index: number, value: string) => console.log(index, value);
  const [openAdvanceFilter, setOpenAdvanceFilter] = useState(false);
  const page = methods.watch('page');
  const accountId = methods.watch('accountId');
  const performSearch = async (values: BasicSearchForm & DiceAdvanceSearch) => {
    setOpenAdvanceFilter(false);
    setState((m) => ({
      ...m,
      appliedFilters: pushSearchFilters(values),
      openAdvanceFilter: false,
    }));
    try {
      setState((m) => ({ ...m, loader: true }));
      const query: DiceSearchModel = constructQuery(values);

      const response = await searchDice(query);
      if (response.status != 200) {
        switch (response.status) {
          case 402:
            setNotification(
              failed(
                'Your job board account has run out of credits. Please contact your Workspace admin to allocate more credits.'
              )
            );
            return;
          case 400 && response.data?.message?.includes('search_phase_execution_exception'):
            setNotification(
              failed(
                'There seems to be an issue with your search string. Please check for special characters and ensure they are properly opened and closed, then try your search again.'
              )
            );
            return;
          case 400:
            setNotification(
              failed('Unable to fetch results. Please check your search string and try again.')
            );
            return;
          case 401:
            setNotification(
              failed(
                'Access denied: Please authenticate yourself to proceed. Make sure you are properly logged in.'
              )
            );
            return;
          case 404:
            setNotification(
              failed('Sorry, but the requested page or resource could not be found.')
            );
            return;
          case 500:
            setNotification(failed('Internal Server Error. Please try again later'));
            return;
          case 502:
            setNotification(failed('Oops! Unable to reach Dice servers. Please try again later.'));
            return;
          case 503:
            setNotification(
              failed('Dice service is temporarily unavailable. Please try again later.')
            );
            return;
          case 504:
            setNotification(failed('Your request has timed out. Please try again later.'));
            return;
          default:
            setNotification(
              failed(
                'There was an error in searching on Dice. Please try again later or contact Wisestep support for assistance.'
              )
            );
            return;
        }
      }

      setState((m) => ({
        ...m,
        diceRequest: query,
        openSourceBot: false,
        showConfigPanel: true,
        showBotConfig: true,
        diceResponse: { ...response.data, existing: JSON.parse(response.data.existing) },
        loader: false,
      }));
      if (response.data?.usage?.viewsRemaining <= 0)
        setNotification(failed('Credits Exhausted, Please contact admin.'));
      if (!response.data?.meta || response.data?.meta?.totalCount === 0)
        setNotification(failed('No Candidates Found'));
    } catch (e) {
      setNotification(
        failed(
          'Error in searching on dice. Please try after sometime, or contact Wisestep support.'
        )
      );
    }
  };

  useEffect(() => {
    if (methods.formState.errors.query?.message) {
      setOpenAdvanceFilter(false);
      setNotification(failed('Search Query is Required'));
    }
  }, [methods.formState.errors.query, setNotification]);

  const onDeleteFilterTag = (tag: string) => {
    const idOfChip = state.appliedFilters.find((existChip) => existChip.tag === tag)?.id ?? '';
    if (!isEmpty(idOfChip)) {
      setAdvanceFilterField(idOfChip as AdvancedSearchKeys, emptyAdvanceFieldValues[idOfChip]);
      methods.handleSubmit(performSearch)();
      const index = state.appliedFilters.findIndex((existChip) => existChip.id === idOfChip);
      state.appliedFilters.splice(index, 1);
      setState((m) => ({
        ...m,
        appliedFilters: [...state.appliedFilters],
      }));
    }
  };

  const handleSourceBotBtnClick = () => {
    if (!state.diceRequest.q) {
      setNotification(
        failed('Please run a candidate search for which you want to create Sourcing bots.')
      );
      return;
    }
    setState((m) => ({
      ...m,
      openSourceBot: true,
      showConfigPanel: false,
      showBotConfig: true,
    }));
  };
  return (
    <FormProvider {...methods}>
      <PageHeader title="Dice" />
      <Grid container>
        <Grid item xs={3}>
          <TalentPoolListSideNav selectedIndexValue={selectedIndex} />
        </Grid>
        <Grid item xs={9}>
          <Box ml={2}>
            <NativeSearchForm
              jobBoardName="DICE_SEARCH"
              performSearch={performSearch}
              openAdvanceFilter={setOpenAdvanceFilter}
              handleSourceBotBtnClick={handleSourceBotBtnClick}
              onViewCvClick={async (candidateId) => {
                try {
                  const candidateData = await fetchJobBoardCandidate({
                    jobBoardAccountId: accountId,
                    jobBoardId: candidateId,
                    force: false,
                    harvestingId: '',
                  });
                  const queryId = `query${uuidv4()}`;
                  setCvViewSourcePage(ITrackCvViewJobBoards.DICESEARCH);
                  localStorage.setItem(queryId, encodeURI(methods.getValues('query') ?? ''));
                  openUrlInNewTab(
                    `/candidate-layout/profile?id=${
                      candidateData?.newCandidateData.id
                    }&array=${[]}&query=${queryId}`
                  );
                } catch (error) {
                  const err = error as AxiosError;
                  if (err.response?.data.status) {
                    setNotification(failed(err.response?.data.status));
                  } else {
                    setNotification(failed('There was some problem in fetching the candidate.'));
                  }
                }
              }}
            />
          </Box>
          <AdvanceFilter
            performSearch={performSearch}
            open={openAdvanceFilter}
            onClose={() => setOpenAdvanceFilter(false)}
          />
          {state.showBotConfig && (
            <BotCustomization
              name={state.diceRequest?.q}
              request={state.diceRequest}
              jobBoardName="DICE_SEARCH"
              accountId={accountId}
              openSourceBot={state.openSourceBot}
              showConfigPanel={state.showConfigPanel}
              setOpenSourceBotBool={(val: boolean) =>
                setState((m) => ({ ...m, openSourceBot: val }))
              }
              setShowConfigPanelBool={(val: boolean) =>
                setState((m) => ({ ...m, showConfigPanel: val }))
              }
              queryError={methods.watch('query')}
            />
          )}
          <WsFilterTags filtersList={state.appliedFilters} onDelete={onDeleteFilterTag} />
          {state.diceResponse?.meta && state.diceResponse.meta.totalCount > 0 && !state.loader ? (
            <Box>
              <WsPagination
                count={Math.ceil(state.diceResponse.meta.totalCount / 50)}
                onChange={(_, value) => {
                  methods.setValue('page', value + 1);
                  methods.handleSubmit(performSearch)();
                }}
                itemsPerPage={50}
                pageNumber={page - 1}
                defaultPage={page}
                isPageCount={true}
                type="Candidates"
                totalCount={state.diceResponse.meta.totalCount}
              />
              <Box>
                {state.diceResponse?.data?.map((diceCandidate) => (
                  <Grid my={2} key={diceCandidate.id} item xs={12}>
                    <CandidateCard
                      candidateId={diceCandidate.id}
                      title={diceCandidate.fullName ?? ''}
                      name={diceCandidate.fullName ?? ''}
                      imgSrc={diceCandidate.imageUrl ?? ''}
                      isDownloaded={state.diceResponse.existing.some(
                        (existingCandidate) =>
                          existingCandidate.candidateJobBoardId === diceCandidate.id
                      )}
                      skills={diceCandidate.skills.map((skill) => skill.skill)}
                      dateLastActive={diceCandidate.dateLastActive}
                      dateResumeLastUpdated={diceCandidate.dateResumeLastUpdated}
                      onViewCvClick={async (candidateId) => {
                        try {
                          const data = await fetchJobBoardCandidate({
                            jobBoardAccountId: accountId,
                            jobBoardId: candidateId,
                            force: false,
                            harvestingId: '',
                          });
                          const candidateData = data.newCandidateData;
                          const params: IQueryParam = {
                            resumeUpdatedOn: diceCandidate.dateResumeLastUpdated,
                            accountId: accountId,
                            jobBoardId: candidateId,
                            query: encodeURI(methods.getValues('query') ?? ''),
                            jobBoard: 'Dice',
                          };
                          const paramId = `param${uuidv4()}`;
                          localStorage.setItem(paramId, getEncodedQueryParams(params));
                          openUrlInNewTab(
                            `/candidate-layout/profile?id=${
                              candidateData?.id
                            }&array=${[]}&q=${getEncodedQueryParams(params)}`
                          );
                        } catch (error) {
                          const err = error as AxiosError;
                          if (err.response?.data.status) {
                            setNotification(failed(err.response?.data.status));
                          } else {
                            setNotification(
                              failed('There was some problem in fetching the candidate.')
                            );
                          }
                        }
                      }}
                      detailItems={getDiceCandidateDeatails(diceCandidate)}
                    />
                  </Grid>
                ))}
              </Box>
            </Box>
          ) : (
            state.loader && <WsListingLoader />
          )}
        </Grid>
      </Grid>
    </FormProvider>
  );
};
export default DiceSearch;
